/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
// Types
import { FilterProps } from 'pages/EventLog/types/eventLog';
// Utils
import { isEmptyObject, useSetState } from 'utils';
// Auth Context
import { useAuthState } from 'context/AuthContext';
import { useAppDispatch } from 'context/AppContext';
// Event Context
import { useSalesOrderDispatch, useSalesOrderState } from 'context/SalesOrderContext';
import { setSalesOrderFilters } from 'context/SalesOrderContext/actions';
import { getEvents } from 'context/SalesOrderContext/middleware';
// Elements
import { CheckItem } from 'elements/ListItems';
import { SearchField } from 'elements/Forms';
import Button from 'elements/Button';
import DateDropdown from 'elements/DateDropdown';
// Components
import SalesOrderFilter from './SalesOrderFilter';
// Style
import styles from './style/salesOrderFilterBar.module.css';
// Config
import { SupplierId, WarehouseId } from '../config';

function SalesOrderFilterBar({ setDisabled }: { setDisabled: Function }) {
  // Auth State
  const { defaultOrganisation: { orgRef } } = useAuthState();
  // App State
  const appDispatch = useAppDispatch();
  // Event State
  const { salesOrderFilters } = useSalesOrderState();
  const salesOrderDispatch = useSalesOrderDispatch();
  const defaultDropdownState = useRef({
    supplierNo: fetchSupplierId(),
    warehouseId: fetchWarehouseId(),
  });
  // State
  const defaultState = useMemo(() => ({
    date: [],
    fromDate: moment().format('DD MMM YYYY'),
    toDate: '',
    creationDate: '',
    limit: '10',
    offSet: 0,
    accountDescription: '',
    customerPONumber: '',
    sourceProductNumber: '',
    supplierSelectedNo: '',
    warehouseSelectedId: defaultDropdownState.current.warehouseId[0],
    processingStatus: ['UNPROCESSED', 'PROCESSING', 'PROCESSED', 'REPROCESS'],
    orgRef,
  }), []);

  const [state, setState] = useSetState(isEmptyObject(salesOrderFilters) ? defaultState : salesOrderFilters);

  const getData = useCallback(async (filters) => {
    if ( salesOrderFilters.offSet !== filters.offSet || filters.offSet === 0) {
      await getEvents({
        appDispatch,
        salesOrderDispatch,
        filters,
        orgRef,
      });
      setDisabled(false);
    }
  }, [salesOrderDispatch, orgRef]);

  async function handleClear() {
    salesOrderDispatch(setSalesOrderFilters(defaultState));
    setState(defaultState);
  }

  async function handleUpdate() {
    salesOrderDispatch(setSalesOrderFilters(state));
  }

  function warehouseIdSelector(activity: string, warehouseSelectedId: string) {
    if(activity === 'ALL') {
      return activity;
    } else if(warehouseSelectedId.includes(activity)) {
      warehouseSelectedId = warehouseSelectedId.replace(`${activity},`, '');
      return warehouseSelectedId ? warehouseSelectedId : 'ALL';
    } else if(warehouseSelectedId.includes('ALL')) {
        return `${activity},`;
    } else {
       return warehouseSelectedId + `${activity},`;
    }
  }

  function supplierNoSelector(activity: string, supplierSelectedNo: string) {
    if(supplierSelectedNo.includes(activity)) {
      return supplierSelectedNo.replace(`${activity},`, '');
    } else {
       return supplierSelectedNo + `${activity},`;
    }
  }

  function renderERPIdFilters(activity: string) {
    return (
      <CheckItem
        borderInset={1}
        checked={state.warehouseSelectedId.includes(activity)}
        handler={() => setState({
          warehouseSelectedId: warehouseIdSelector(activity, state.warehouseSelectedId),
        })}
        key={activity}
        label={activity}
        name={activity}
        value={activity}
      />
    );
  }

  function renderSupplierIdFilters(activity: string) {
    return (
      <CheckItem
        borderInset={1}
        checked={state.supplierSelectedNo.includes(activity)}
        handler={() => setState({
          supplierSelectedNo: supplierNoSelector(activity, state.supplierSelectedNo),
        })}
        key={activity}
        label={activity}
        name={activity}
        value={activity}
      />
    );
  }

  function fetchSupplierId() {
    let supplierId: string[] = [];
    for (const [key, item] of Object.entries(SupplierId)) {
      if (orgRef === key) {
        supplierId = item.map((i: { supplierId: string }) => i.supplierId)      
      }       
    }
    return supplierId;
  }

  function fetchWarehouseId() {
    let warehouseId: string[] = [];
    for (const [key, item] of Object.entries(WarehouseId)) {
      if (orgRef === key) {
        warehouseId = item.map((i: { displayName: string }) => i.displayName)
      }       
    }
    return warehouseId;
  }

  const handleUpdateAuto = useCallback(async (filters:FilterProps) => {
    salesOrderDispatch(setSalesOrderFilters(filters));
  }, [salesOrderDispatch]);

  useEffect(() => {
    if (!isEmptyObject(salesOrderFilters) && salesOrderFilters.orgRef === defaultState.orgRef) {
      getData(salesOrderFilters);
      setState(salesOrderFilters);
    } else {
      handleUpdateAuto(defaultState);
    }
  }, [defaultState, salesOrderFilters, getData, handleUpdateAuto]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <SalesOrderFilter
          label="ERP Warehouse ID"
          required={true}
          value={
            state.warehouseSelectedId.charAt(state.warehouseSelectedId.length - 1) === ','
              ? state.warehouseSelectedId.slice(0, -1)
              : state.warehouseSelectedId
          }
        >
          { defaultDropdownState.current.warehouseId.map(renderERPIdFilters) }
        </SalesOrderFilter>
        <SalesOrderFilter
          label="Shipment Date"
          required={true}
          value={
            (state.fromDate)
              ? `${state.fromDate} ${state.toDate ? '-' : ''} ${state.toDate}`
              : 'Select'
          }
        >
          
          <div className={styles.dates}>
            <div>
              <DateDropdown
                dateFormat="DD MMM YYYY"
                isClearable={false}
                minDate={state.toDate ? moment(state.toDate).add(-3, 'day') : null}
                maxDate={state.toDate ? moment(state.toDate).add(-1, 'day') : null}
                onChange={(date:string) => setState({
                  fromDate: moment(date).format('DD MMM YYYY'),
                })}
                placeholder="From Date"
                selected={state.fromDate ? moment(state.fromDate) : null}
                value=""
              />
            </div>
            <div>
              <DateDropdown
                dateFormat="DD MMM YYYY"
                isClearable={true}
                minDate={state.fromDate ? moment(state.fromDate) : null}
                maxDate={state.fromDate ? moment(state.fromDate).add(3, 'day') : null}
                onChange={(date:string) => setState({
                  toDate: date ? moment(date).format('DD MMM YYYY') : '',
                })}
                placeholder="To Date"
                selected={state.toDate ? moment(state.toDate) : null}
                value=""
              />
            </div>
          </div>
        </SalesOrderFilter>
        <SalesOrderFilter
          label="Supplier Number"
          required={false}
          value={
            state.supplierSelectedNo.charAt(state.supplierSelectedNo.length - 1) === ','
              ? state.supplierSelectedNo.slice(0, -1)
              : state.supplierSelectedNo
          }
        >
          { defaultDropdownState.current.supplierNo.map(renderSupplierIdFilters) }
        </SalesOrderFilter>
        <DateDropdown
          dateFormat="DD MMM YYYY"
          isClearable={false}
          minDate={null}
          maxDate={moment()}
          onChange={(date:string) => setState({
            creationDate: moment(date).format('DD MMM YYYY'),
          })}
          placeholder="Creation Date"
          selected={state.creationDate ? moment(state.creationDate) : null}
          value=""
          icon="Calendar"
        />
        <SearchField
          icon={false}
          keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ accountDescription: e.target.value })}
          onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ accountDescription: e.target.value })}
          onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ accountDescription: e.target.value })}
          placeholderText="Location ID"
          value={state.accountDescription}
        />
        <SearchField
          icon={false}
          keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerPONumber: e.target.value })}
          onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerPONumber: e.target.value })}
          onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerPONumber: e.target.value })}
          placeholderText="Customer PO Number"
          value={state.customerPONumber}
        />
        <SearchField
          icon={false}
          keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ sourceProductNumber: e.target.value })}
          onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ sourceProductNumber: e.target.value })}
          onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ sourceProductNumber: e.target.value })}
          placeholderText="PIN Code"
          value={state.sourceProductNumber}
        />
      </div>
      <div className={styles.actions}>
        <Button
          btnStyle="light"
          disabled={JSON.stringify(state) === JSON.stringify(defaultState)}
          handler={() => handleClear()}
        >
          Clear
        </Button>
        <Button
          btnStyle="secondary"
          disabled={(!state.fromDate ||  !state.warehouseSelectedId)}
          handler={() => handleUpdate()}
        >
          Update
        </Button>
      </div>
    </div>
  );
}

export default SalesOrderFilterBar;